import { SPORTS } from '../../data/constants';
import { legacySportKey } from '../../data/sports';
import { StatCards, SectionCard, WeeklyBarChart, HBar, last8Weeks, parseDateStr } from './shared';

const toMonthKey = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

export default function OverviewAnalysis({ records, weights }) {
    if (!records.length) {
        return (
            <div className="empty-state" style={{ padding: '48px 0' }}>
                <div className="empty-text">아직 운동 기록이 없어요.<br />기록을 추가하면 분석이 시작돼요.</div>
            </div>
        );
    }

    // 연속 운동일
    const streak = (() => {
        const days = [...new Set(records.map(r => r.date))].sort().reverse();
        const today = new Date(); today.setHours(0, 0, 0, 0);
        let s = 0;
        for (let i = 0; i < days.length; i++) {
            const rd = new Date(days[i]); rd.setHours(0, 0, 0, 0);
            if (Math.round((today - rd) / 86400000) === i) s++;
            else break;
        }
        return s;
    })();

    const totalMin = records.reduce((a, r) => a + (+r.duration || 0), 0);
    const avgCond = records.length ? (records.reduce((a, r) => a + (+r.condScore || 3), 0) / records.length).toFixed(1) : '-';
    const avgFat = records.length ? (records.reduce((a, r) => a + (+r.fatScore || 3), 0) / records.length).toFixed(1) : '-';

    const weeklyFreq = last8Weeks(records);
    const currentWeek = weeklyFreq[weeklyFreq.length - 1]?.val || 0;
    const prevWeek = weeklyFreq[weeklyFreq.length - 2]?.val || 0;
    const weekDiff = currentWeek - prevWeek;

    // 종목별 카운트
    const sportCounts = {};
    records.forEach(r => {
        const k = legacySportKey(r);
        sportCounts[k] = (sportCounts[k] || 0) + 1;
    });
    const sortedSports = Object.entries(sportCounts)
        .filter(([k]) => SPORTS[k])
        .sort((a, b) => b[1] - a[1])
        .slice(0, 8);
    const maxSport = sortedSports[0]?.[1] || 1;

    // 최근 6개월 운동 시간
    const monthlyTime = {};
    records.forEach(r => {
        const key = toMonthKey(parseDateStr(r.date));
        monthlyTime[key] = (monthlyTime[key] || 0) + (+r.duration || 0);
    });
    const last6Months = Array.from({ length: 6 }, (_, i) => {
        const d = new Date();
        d.setMonth(d.getMonth() - (5 - i));
        const key = toMonthKey(d);
        return { label: `${d.getMonth() + 1}월`, val: Math.round((monthlyTime[key] || 0) / 60) };
    });
    const maxMonth = Math.max(...last6Months.map(m => m.val), 1);

    const topSport = sortedSports[0];

    return (
        <>
            <div className="insight-card">
                <div>
                    <div className="insight-title">이번 주 {currentWeek}회 운동</div>
                    <div className="insight-desc">
                        {weekDiff === 0 ? '지난주와 같은 페이스예요' : `지난주보다 ${weekDiff > 0 ? '+' : ''}${weekDiff}회`}
                        {topSport ? ` · 최다 종목 ${SPORTS[topSport[0]]?.name || topSport[0]}` : ''}
                    </div>
                </div>
                <div className="insight-pill">🔥 {streak}일 연속</div>
            </div>

            <StatCards items={[
                { val: `${records.length}개`, label: '총 기록' },
                { val: `${streak}일`, label: '연속 운동일' },
                { val: `${Math.round(totalMin / 60)}h`, label: '총 시간' },
                { val: `${avgCond}/5`, label: '평균 컨디션' },
            ]} />

            <SectionCard title="운동 흐름" desc="주간 운동 횟수와 월별 운동 시간">
                <WeeklyBarChart data={weeklyFreq} label="주간 운동 횟수" color="var(--accent)" />

                <div className="chart-wrap">
                    <div className="chart-title">
                        <span>월별 운동 시간</span>
                        <span style={{ fontFamily: 'DM Mono,monospace', color: 'var(--accent)' }}>
                            {last6Months[last6Months.length - 1]?.val}h
                        </span>
                    </div>
                    <div className="bar-chart" style={{ alignItems: 'flex-end', gap: 4 }}>
                        {last6Months.map((m, i) => {
                            const isLatest = i === last6Months.length - 1;
                            const h = Math.round((m.val / maxMonth) * 72) + 4;
                            return (
                                <div key={i} className="chart-bar-wrap">
                                    {m.val > 0 && (
                                        <span style={{ fontSize: 10, fontFamily: 'DM Mono,monospace', color: isLatest ? 'var(--accent)' : 'var(--text2)', marginBottom: 3 }}>
                                            {m.val}h
                                        </span>
                                    )}
                                    <div className="chart-bar" style={{ height: h, background: m.val ? (isLatest ? 'var(--accent)' : 'var(--bg4)') : 'var(--bg3)' }} />
                                    <div className="chart-label" style={{ color: isLatest ? 'var(--text2)' : 'var(--text3)' }}>{m.label}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </SectionCard>

            <SectionCard title="종목 분포" desc="지금까지 가장 많이 한 운동">
                {sortedSports.length === 0 ? (
                    <div style={{ fontSize: 12, color: 'var(--text3)', padding: '12px 0' }}>기록 없음</div>
                ) : (
                    <div style={{ marginTop: 4 }}>
                        {sortedSports.map(([k, cnt]) => (
                            <HBar key={k} label={SPORTS[k]?.name || k} value={cnt} max={maxSport} color={SPORTS[k]?.color || 'var(--accent)'} suffix="회" />
                        ))}
                    </div>
                )}
            </SectionCard>

            <SectionCard title="회고 평균" desc="전체 기록의 컨디션/강도 평균">
                <div style={{ display: 'flex', gap: 16, marginTop: 4 }}>
                    <div className="stat-card" style={{ flex: 1 }}>
                        <div className="stat-val">{avgCond}</div>
                        <div className="stat-label">평균 컨디션</div>
                    </div>
                    <div className="stat-card" style={{ flex: 1 }}>
                        <div className="stat-val">{avgFat}</div>
                        <div className="stat-label">평균 운동강도</div>
                    </div>
                    <div className="stat-card" style={{ flex: 1 }}>
                        <div className="stat-val">{Math.round(totalMin / records.length)}분</div>
                        <div className="stat-label">평균 운동시간</div>
                    </div>
                </div>
            </SectionCard>
        </>
    );
}
